import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
    Table,
    Alert,
    Spinner,
    Badge,
    Card,
    Row,
    Col
} from 'react-bootstrap';
import {
    FaFileAlt,
    FaFilePdf,
    FaFileImage,
    FaUpload,
    FaEye,
    FaDownload,
    FaTrash,
    FaTimes
} from 'react-icons/fa';
import api from '../../services/api';

/**
 * Report Extra Document Modal
 * 
 * Bu modal sadece Report listesindeki B butonuna özel
 * Ekstra belgeler yüklemek için kullanılır
 * Tüm belgeler "diger" kategorisinde saklanır
 */
const ReportExtraDocumentModal = ({
    show,
    onHide,
    reportId,
    customerName,
    onUploadSuccess
}) => {
    // State management
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Upload state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadNotes, setUploadNotes] = useState('');

    /**
     * Modal açıldığında belgeleri yükle
     */
    useEffect(() => {
        if (show && reportId) {
            fetchDocuments();
        }
    }, [show, reportId]);

    /**
     * Report'a ait ekstra belgeleri getir
     * Sadece "diger" kategorisindeki belgeler
     */
    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/documents/report/${reportId}`, {
                params: { category: 'diger' }
            });

            setDocuments(response.data.documents || []);
        } catch (error) {
            console.error('Belgeler yüklenirken hata:', error);
            setError('Belgeler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Dosya seçimi handler
     */
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);

        // Hata mesajını temizle
        setError('');
    };

    /**
     * Dosya yükleme işlemi - DEBUG VERSION
     * Her dosya için ayrı API çağrısı yapar
     */
    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError('Lütfen dosya seçiniz');
            return;
        }

        console.log('🚀 Upload başlıyor:', {
            reportId,
            fileCount: selectedFiles.length,
            notes: uploadNotes
        });

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            const uploadedFiles = [];

            // Her dosyayı tek tek yükle
            for (const file of selectedFiles) {
                console.log('📤 Dosya yükleniyor:', {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type
                });

                const formData = new FormData();
                formData.append('document', file);
                formData.append('category', 'diger');
                formData.append('title', file.name);
                formData.append('reportId', reportId);

                if (uploadNotes.trim()) {
                    formData.append('notes', uploadNotes);
                }

                // FormData debug
                console.log('📋 FormData içeriği:');
                for (const [key, value] of formData.entries()) {
                    if (value instanceof File) {
                        console.log(key, `File: ${value.name} (${value.size} bytes)`);
                    } else {
                        console.log(key, value);
                    }
                }

                // Fetch API kullan - Axios sorunlu
                const token = localStorage.getItem('token');
                const response = await fetch('/api/documents/upload', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || 'Yükleme başarısız');
                }

                console.log('✅ Upload response:', result);

                if (result.success) {
                    uploadedFiles.push(result.document);
                }
            }

            // Başarı durumu
            if (uploadedFiles.length > 0) {
                setSuccess(`${uploadedFiles.length} belge başarıyla yüklendi`);

                // Form temizle
                setSelectedFiles([]);
                setUploadNotes('');
                clearFileInput();

                // Belge listesini yenile
                await fetchDocuments();

                // Parent component'e bildir
                if (onUploadSuccess) {
                    onUploadSuccess(uploadedFiles);
                }
            }
        } catch (error) {
            console.error('❌ Yükleme hatası:', error);

            const errorMsg = error.message || 'Dosya yüklenirken hata oluştu';
            setError(errorMsg);
        } finally {
            setUploading(false);
        }
    };

    /**
     * File input'u temizle
     */
    const clearFileInput = () => {
        const fileInput = document.getElementById('extra-document-upload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    /**
     * Belge silme işlemi - Cloudinary'den de siler
     */
    const handleDeleteDocument = async (document) => {
        if (!window.confirm(`"${document.title}" belgesini silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz!`)) {
            return;
        }

        try {
            console.log('🗑️ Belge siliniyor:', {
                documentId: document._id,
                fileName: document.title,
                cloudinaryPublicId: document.file?.cloudinaryPublicId
            });

            const response = await api.delete(`/documents/${document._id}`);

            console.log('✅ Silme response:', response.data);

            if (response.data.success) {
                setSuccess('Belge başarıyla silindi (Cloudinary\'den de temizlendi)');
                await fetchDocuments(); // Listeyi yenile
            }
        } catch (error) {
            console.error('❌ Silme hatası:', error);
            console.error('❌ Error details:', error.response?.data);
            setError(error.response?.data?.message || 'Belge silinirken hata oluştu');
        }
    };

    /**
     * Belge görüntüleme - Google Docs Viewer ile
     */
    const handleViewDocument = (document) => {
        if (document.file?.cloudinaryUrl) {
            // ✅ Google Docs Viewer kullan (PDF'leri browser'da gösterir)
            const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(document.file.cloudinaryUrl)}&embedded=true`;
            window.open(googleViewerUrl, '_blank', 'noopener,noreferrer');
        }
    };

    /**
     * Belge indirme - Orijinal formatında (FİX)
     */
    const handleDownloadDocument = async (doc) => {
        try {
            const response = await fetch(doc.file.cloudinaryUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // ✅ window.document kullan (conflict fix)
            const a = window.document.createElement('a');
            a.href = url;

            // ✅ Orijinal dosya adını ve uzantısını koru
            a.download = doc.file.originalName || doc.title;

            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('İndirme hatası:', error);
            setError('Dosya indirilirken hata oluştu');
        }
    };

    /**
     * Dosya ikonu belirleme
     */
    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') return FaFilePdf;
        if (mimeType?.startsWith('image/')) return FaFileImage;
        return FaFileAlt;
    };

    /**
     * Dosya boyutu formatla
     */
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    /**
     * Modal kapanma handler
     */
    const handleClose = () => {
        // Form temizle
        setSelectedFiles([]);
        setUploadNotes('');
        setError('');
        setSuccess('');
        clearFileInput();

        // Modal'ı kapat
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    📎 Ekstra Belgeler - {customerName}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Error & Success Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Upload Section */}
                <Card className="mb-4">
                    <Card.Header>
                        <h6 className="mb-0">📤 Yeni Belge Yükle</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row className="align-items-end">
                            <Col md={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Dosya Seç *</Form.Label>
                                    <Form.Control
                                        id="extra-document-upload"
                                        type="file"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                    />
                                    <Form.Text className="text-muted">
                                        PDF, JPG, PNG, GIF, WEBP dosyaları desteklenir
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Notlar</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Opsiyonel açıklama"
                                        value={uploadNotes}
                                        onChange={(e) => setUploadNotes(e.target.value)}
                                        disabled={uploading}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Button
                                    variant="success"
                                    onClick={handleUpload}
                                    disabled={selectedFiles.length === 0 || uploading}
                                    className="w-100 mb-3"
                                >
                                    {uploading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Yükleniyor...
                                        </>
                                    ) : (
                                        <>
                                            <FaUpload className="me-2" />
                                            Yükle
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>

                        {/* Selected Files Preview */}
                        {selectedFiles.length > 0 && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <strong>Seçili Dosyalar ({selectedFiles.length}):</strong>
                                <ul className="mb-0 mt-2">
                                    {selectedFiles.map((file, index) => (
                                        <li key={index} className="small text-muted">
                                            {file.name} ({formatFileSize(file.size)})
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Documents List */}
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">📋 Yüklenen Belgeler</h6>
                        <Badge bg="primary">{documents.length} belge</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <div className="mt-2">Belgeler yükleniyor...</div>
                            </div>
                        ) : documents.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <FaFileAlt size={32} className="mb-2" />
                                <p className="mb-0">Henüz ekstra belge yüklenmemiş</p>
                                <small>Yukarıdaki formu kullanarak belge ekleyebilirsiniz</small>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table striped hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Dosya</th>
                                            <th>Yüklenme Tarihi</th>
                                            <th>Yükleyen</th>
                                            <th width="160">İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documents.map((document) => {
                                            const FileIcon = getFileIcon(document.file?.mimeType);

                                            return (
                                                <tr key={document._id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <FileIcon className="me-2 text-primary" />
                                                            <div>
                                                                <div className="fw-bold">
                                                                    {document.title}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {formatFileSize(document.file?.size)}
                                                                    {document.notes && (
                                                                        <> • {document.notes}</>
                                                                    )}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {new Date(document.createdAt).toLocaleDateString('tr-TR')}
                                                            <br />
                                                            {new Date(document.createdAt).toLocaleTimeString('tr-TR', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {document.uploadedBy?.name || 'Bilinmiyor'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => handleViewDocument(document)}
                                                                title="Görüntüle"
                                                            >
                                                                <FaEye size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-success"
                                                                onClick={() => handleDownloadDocument(document)}
                                                                title="İndir"
                                                            >
                                                                <FaDownload size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDeleteDocument(document)}
                                                                title="Sil"
                                                            >
                                                                <FaTrash size={12} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Kapat
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReportExtraDocumentModal;